import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useActiveCompany } from "@/hooks/use-active-company";
import { ArrowRight } from "lucide-react";
import { useCompanyProfile } from "@/hooks/use-company-profile";
import { ACTIVATABLE_MODULES, type ModuleKey } from "@/modules";
import type { ModuleWorkspaceTarget } from "@/modules/contract";
import { getCapabilities } from "@/modules/capabilities";


export const Route = createFileRoute("/_authenticated/portal")({
  head: () => ({
    meta: [
      { title: "Portal | VERQO" },
      { name: "description", content: "Velg modulen du vil jobbe i." },
    ],
  }),
  component: PortalPage,
});

// Områder plattformen kjenner til. Moduler som selv eier området vises som
// aktive arbeidsflater; resten står igjen som «Kommer senere» for den modulen.
const KNOWN_AREAS = [
  { key: "kalkyle", label: "Kalkyle", emoji: "📐" },
  { key: "prosjekt", label: "Prosjektstyring", emoji: "📋" },
  { key: "hms", label: "HMS", emoji: "🦺" },
  { key: "ks", label: "KS", emoji: "✅" },
  { key: "fdv", label: "FDV", emoji: "📘" },
] as const;

function PortalPage() {
  const navigate = useNavigate();
  const { setActiveModule, activeModule } = useActiveCompany();
  const { data: company } = useCompanyProfile();

  function openModule(kind: ModuleKey) {
    setActiveModule(kind);
    navigate({ to: "/dashboard" });
  }

  const workspaceAreas = activeModule.workspaceAreas ?? [];
  const caps = getCapabilities(activeModule.key);
  // Områder faget ikke har i det hele tatt skal heller ikke stå som «kommer».
  const relevantArea = (key: string) => {
    if (key === "kalkyle") return caps.hasCalculations || caps.hasQuotes;
    if (key === "hms" || key === "ks") return caps.hasCompliance;
    if (key === "fdv") return caps.hasFdv;
    return true;
  };
  const upcoming = KNOWN_AREAS.filter(
    (a) => relevantArea(a.key) && !workspaceAreas.some((w) => w.key === a.key),
  );

  function openArea(target: ModuleWorkspaceTarget) {
    if (target.kind === "route") {
      navigate({ to: target.to as never });
      return;
    }
    navigate({ to: "/dokumentasjon/$area", params: { area: target.area } });
  }



  return (
    <div className="mx-auto max-w-5xl">

        <div className="mb-12 text-center">
          <h1 className="text-5xl font-bold tracking-tight sm:text-6xl lg:text-7xl">
            Velkommen til
            <span className="block text-primary">VERQO</span>
          </h1>
          <p className="mt-5 text-lg text-muted-foreground sm:text-xl">
            Velg modulen du vil jobbe i.
          </p>
        </div>


        <div className="grid gap-5 sm:grid-cols-2">
          {ACTIVATABLE_MODULES.map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.key}
                onClick={() => openModule(t.key)}
                className="group relative overflow-hidden rounded-2xl border border-border bg-card p-8 text-left shadow-sm transition-all hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-lg"
              >
                <div
                  className={`pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-gradient-to-br ${t.portal.gradient} blur-2xl transition-opacity group-hover:opacity-100`}
                />
                <div className="relative">
                  <div className="mb-6 grid h-14 w-14 place-items-center rounded-xl bg-foreground text-primary-foreground">
                    <Icon className="h-7 w-7" strokeWidth={2.2} />
                  </div>
                  <div className="mb-1 flex items-center gap-2">
                    <h2 className="text-2xl font-semibold tracking-tight">
                      {t.emoji} {t.label}
                    </h2>
                  </div>
                  <p className="mb-6 text-sm text-muted-foreground">{t.portal.tagline}</p>
                  <div className="flex items-center gap-1.5 text-sm font-medium text-primary">
                    Åpne modul
                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {workspaceAreas.length > 0 && (
          <div className="mt-12">
            <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
              <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                Arbeidsområder i {activeModule.label}
              </h3>
              <span className="text-xs text-muted-foreground">Aktive</span>
            </div>
            <div className="grid grid-cols-1 gap-3 min-[400px]:grid-cols-2 sm:grid-cols-3 lg:grid-cols-5">
              {workspaceAreas.map((a) => (
                <button
                  key={a.key}
                  type="button"
                  onClick={() => openArea(a.target)}
                  className="rounded-xl border border-border bg-card p-4 text-left transition-all hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-md"
                >
                  <div className="text-2xl">{a.emoji}</div>
                  <div className="mt-1 text-sm font-semibold">{a.label}</div>
                  <div className="mt-1 text-xs leading-snug text-muted-foreground">
                    {a.description}
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {upcoming.length > 0 && (
          <div className="mt-10">
            <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
              <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                Kommer senere
              </h3>
              <span className="text-xs text-muted-foreground">Under planlegging</span>
            </div>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
              {upcoming.map((m) => (
                <div
                  key={m.key}
                  className="rounded-xl border border-dashed border-border bg-muted/30 p-4 text-center opacity-70"
                >
                  <div className="text-2xl">{m.emoji}</div>
                  <div className="mt-1 text-sm font-medium">{m.label}</div>
                </div>
              ))}
            </div>
          </div>
        )}



        <footer className="mt-14 border-t border-border pt-6 text-center text-xs text-muted-foreground">
          {company?.company_name || "VERQO"}
          {company?.org_number ? <>{" • "}Org.nr {company.org_number}</> : null}
          {company?.website ? (
            <>
              {" • "}
              <a href={company.website} className="hover:underline">
                {company.website.replace(/^https?:\/\//, "")}
              </a>
            </>
          ) : null}
        </footer>
      </div>
  );
}

